import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import { personMinimalSchema } from '@/forms/PersonMinimalForm/personMinimalSchema';
import { PersonMinimalFormPayload, PersonMinimalFormValues } from '@/forms/PersonMinimalForm/types';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonRepository from '@/repositories/PersonRepository';
import { PersonMinimal } from '@/types/person';
import Button from '@/ui/components/Button';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Toggle from '@/ui/components/Form/Toggle';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

/**
 * Form to create a PersonMinimal object
 * No current Edit functionality
 */
const PersonMinimalForm = () => {
  const { addMessage } = useContext(AppContext);
  const personRepository = PersonRepository();
  const { navigateToProfileWithId } = useNavigateToPage();

  const initialValues: PersonMinimalFormValues = {
    email: '',
    exclude_from_analytics: false,
    first_name: '',
    last_name: '',
    phone: ''
  };

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(personMinimalSchema)
  });

  const createPerson = useMutation({
    mutationFn: ({ values }: { values: PersonMinimalFormPayload }) => {
      return personRepository.createPersonMinimal({ values });
    },
    onSuccess: async (person: PersonMinimal) => {
      addMessage({ level: 'success', text: 'Successfully created person' });
      navigateToProfileWithId(person.id);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: PersonMinimalFormValues) => {
    const payload: PersonMinimalFormPayload = {
      email: values.email || null,
      exclude_from_analytics: values.exclude_from_analytics,
      first_name: values.first_name,
      last_name: values.last_name || null,
      phone: values.phone || null
    };

    createPerson.mutate({ values: payload });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <InputGroup>
        <Controller
          name="first_name"
          control={control}
          render={({ field }) => {
            return (
              <InputField
                label="First Name *"
                id="first-name"
                placeholder="First Name"
                type="text"
                {...field}
              />
            );
          }}
          rules={{ required: 'First Name is required' }}
        />

        <FieldError message={errors.first_name?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="last_name"
          control={control}
          render={({ field }) => {
            return (
              <InputField
                label="Last Name"
                id="last-name"
                placeholder="Last Name"
                type="text"
                {...field}
              />
            );
          }}
        />

        <FieldError message={errors.last_name?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="email"
          control={control}
          render={({ field }) => {
            return (
              <InputField
                label="Email"
                id="email"
                placeholder="Email address"
                type="text"
                {...field}
              />
            );
          }}
        />

        <FieldError message={errors.email?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => {
            return (
              <InputField
                label="Phone"
                id="phone"
                placeholder="Phone number"
                type="text"
                {...field}
              />
            );
          }}
        />

        <FieldError message={errors.phone?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="exclude_from_analytics"
          control={control}
          render={({ field }) => {
            return (
              <Toggle
                enabled={!!field.value}
                onChange={field.onChange}
                label="Exclude from analytics"
                enabledIcon={faCheck}
              />
            );
          }}
        />

        <FieldError message={errors.exclude_from_analytics?.message} />
      </InputGroup>

      <InputGroup>
        <Button color={Color.BLUE} loading={createPerson.isPending} type="submit">
          Save
        </Button>
      </InputGroup>
    </form>
  );
};

export default PersonMinimalForm;
